import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Dynamic languages have jumped the shark",
  "description": "",
  "published": "2013-05-31T00:00:00.000Z",
  "redirect_from": ["/blog/dynamic-languages-have-jumped-the-shark/swizec/6204"],
  "categories": "Dynamic programming language, Java, JavaScript, Read–eval–print loop, Ruby, Scala, Type system, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://www.filepicker.io/api/file/jJuq5mNjQbC0ZJrdJYms",
        "alt": "Jumping the shark"
      }}></img></p>
    <p>{`I still remember the heated arguments I'd have with my high school professors about dynamic languages. What do you mean python isn't a `}<em parentName="p">{`real`}</em>{` language? What's wrong with you!? `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Dynamic_programming_language",
        "title": "Dynamic programming language"
      }}>{`Dynamic languages`}</a>{` are the coolest thing ever!`}</p>
    <p>{`Some kids fight about curfews and school rules, I fought about `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Scripting_language",
        "title": "Scripting language"
      }}>{`scripting languages`}</a>{` being just as "real" as the likes of C and Pascal. That was ten years ago - dynamic languages were all the rage and I just started exploring the world of programming beyond what my mentors laid out in front of me.`}</p>
    <p>{`Nimble scripting languages sounded like a promised land compared to the musky static languages of yore. No longer would you be forced to think about data types all the time, no longer would your code be littered with the `}<inlineCode parentName="p">{`int`}</inlineCode>{` and `}<inlineCode parentName="p">{`char`}</inlineCode>{` and `}<inlineCode parentName="p">{`double`}</inlineCode>{` of the world, no more stifling constraints on using a variable once it's been defined.`}</p>
    <p>{`Just a pure expression of thought. Programmer and problem at peace with one another, no angry compiler will get in your way.`}</p>
    <h2 {...{
      "id": "the-promised-land-tumbles"
    }}>{`The promised land tumbles`}</h2>
    <p>{`Yesterday I realised dynamic languages have jumped the shark. The evidence has been mounting for a while, but yesterday's mishap with `}<a parentName="p" {...{
        "href": "http://rubyonrails.org",
        "title": "Ruby on Rails"
      }}>{`Ruby on Rails`}</a>{` sealed the deal.`}</p>
    <p>{`Did you know that Rails environment settings, the `}<inlineCode parentName="p">{`ENV`}</inlineCode>{` hash, only accepts string settings? I wanted to make a boolean flag. The only solution was to make a string `}<inlineCode parentName="p">{`'false'`}</inlineCode>{`, then do a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Comparison_of_programming_languages_%28string_functions%29",
        "title": "Comparison of programming languages (string functions)"
      }}>{`string comparison`}</a>{` when needed. There isn't even a native way to cast a string to a boolean.`}</p>
    <p>{`Programmer left completely to his own devices. Out in the rain. With no chance of escape.`}</p>
    <p>{`The closest Ruby will let you to casting a string to a boolean is using something like `}<inlineCode parentName="p">{`!!'blah'`}</inlineCode>{`, but all strings evaluate to `}<inlineCode parentName="p">{`true`}</inlineCode>{`. Even empty strings.`}</p>
    <p>{`Useless.`}</p>
    <p>{`The situation is just as bad in other dynamic languages. `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`JavaScript`}</a>{` is particularly famous for its weird handling of data types.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`And this, of course, evaluates to the string "fail".  (![]+[])[+[]]+(![]+[])[+!+[]]+([![]]+[][[]])[+!+[]+[+[]]]+(![]+[])[!+[]+!+[]];`}</p>{`— Lagergren (@lagergren) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/lagergren/status/337484475204255744"
      }}>{`May 23, 2013`}</a></blockquote>
    <p>{`Try it. It works.`}</p>
    <p>{`Part of the code returns `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`, which is magically transformed into a string, then other parts of the code select particular characters from that string ... the mind reels in disbelief.`}</p>
    <p>{`I don't want to remember the atrocities I've committed in `}<a parentName="p" {...{
        "href": "http://www.php.net",
        "title": "PHP"
      }}>{`PHP`}</a>{` just to convince it that yes, I do know what I'm doing, yes I do want that variable to behave like it's a particular type.`}</p>
    <p>{`Python's strong typing at least ensures we can't add a string to an integer, but there's still the odd `}<inlineCode parentName="p">{`5/float(2)`}</inlineCode>{` type of hack. Some people suggest going as far as littering your code with assert statements to ensure your team mates can't do stupid things with your functions.`}</p>
    <p>{`This is the exact opposite of what I was promised as an aspiring 15 year old coder.`}</p>
    <h2 {...{
      "id": "why-we-wanted-dynamic-languages-in-the-first-place"
    }}>{`Why we wanted dynamic languages in the first place`}</h2>
    <p><img parentName="p" {...{
        "src": "https://www.filepicker.io/api/file/8xojSjy5S9CpVtWes2dl",
        "alt": null
      }}></img></p>
    <p>{`Dynamic languages promised an ease of development, a sense of thinking about solving the problem instead of worrying about talking to the computer. And the world fell in love.`}</p>
    <p>{`Instead, dynamic languages delivered a hodgepodge so wibbly-wobbly it always forces you to think about data types, even about the particular quirks of how this particular language handles certain conversions. Always at the worst possible moment. Nothing is true anymore, you can't rely on a single thing; without automated tests you're flying blind, relying on nothing but your raw coding prowess to stave off the always imminent failure.`}</p>
    <p>{`God forbid one of your team mates does something stupid.`}</p>
    <p>{`But we did get away from the whole `}<inlineCode parentName="p">{`int a`}</inlineCode>{` nonsense. And we did get fancy `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Read%E2%80%93eval%E2%80%93print_loop",
        "title": "Read–eval–print loop"
      }}>{`REPL`}</a>{`'s. And we did get away from the write-compile-run cycle.`}</p>
    <p>{`But what have the Romans ever done for us!`}</p>
    <h2 {...{
      "id": "static-is-better-now"
    }}>{`Static is better now`}</h2>
    <p>{`Here's the thing, though, there is now a whole new class of even awesomer languages - the modern statically strong typed languages.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.scala-lang.org/",
        "title": "Scala (programming language)"
      }}>{`Scala`}</a>{` and `}<a parentName="p" {...{
        "href": "http://haskell.org"
      }}>{`Haskell`}</a>{` have everything we've always wanted from a dynamic language. You never have to spell out a variable's name, the compiler often knows better than you do anyway, and there is a REPL you can use to prototype things.`}</p>
    <p>{`But unlike dynamic languages you can rely on `}<em parentName="p">{`everything`}</em>{`. All the benefits of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Type_system",
        "title": "Type system"
      }}>{`static type`}</a>{` analysis, most of the benefits of dynamic languages. Hell, you can get rid of the write-compile-run cycle with a simple bash script that compiles your code before running it.`}</p>
    <p>{`Just remember, static languages are not just C and Java anymore. The world has moved on while we were stuck in the dynamic hell hole for the past ten years.`}</p>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=83b08eef-ef3e-4b96-9d39-cf7b256a3e51",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      